import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Network, Building2 } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { typography } from '../../config/typography';
import { spacing } from '../../config/spacing';
import { Card } from '../UI/card';
import { COMPANY_TYPES } from '../../models/watchlistModels';

const COLUMNS = [
  { id: 'raw_material', labelKey: 'kanbanRawMaterial', types: [COMPANY_TYPES.RAW_MATERIAL_SUPPLIER], color: colors.info[500] },
  { id: 'component_equipment', labelKey: 'kanbanComponentEquipment', types: [COMPANY_TYPES.COMPONENT_MANUFACTURER, COMPANY_TYPES.EQUIPMENT_SUPPLIER], color: colors.brand[500] },
  { id: 'finished_packaging', labelKey: 'kanbanFinishedPackaging', types: [COMPANY_TYPES.FINISHED_GOODS_MANUFACTURER, COMPANY_TYPES.PACKAGING_COMPANY], color: colors.secondary[500] },
  { id: 'logistics_services', labelKey: 'kanbanLogisticsServices', types: [COMPANY_TYPES.LOGISTICS_COMPANY, COMPANY_TYPES.SERVICE_PROVIDER, COMPANY_TYPES.UTILITY_COMPANY, COMPANY_TYPES.CONTRACT_MANUFACTURER, COMPANY_TYPES.RESEARCH_DEVELOPMENT_COMPANY], color: colors.success[500] },
  { id: 'unassigned', labelKey: 'kanbanUnassigned', types: ['unassigned'], color: colors.gray[400] },
];

function getColumnForType(companyType) {
  const raw = (companyType ?? '').toString().trim().toLowerCase();
  const col = COLUMNS.find((c) =>
    c.types.some((t) => String(t).toLowerCase() === raw || (t === 'unassigned' && !raw))
  );
  return col ? col.id : 'unassigned';
}

function normalizeCompanyId(id) {
  if (id == null || typeof id === 'boolean') return null;
  const s = String(id).trim();
  if (!s || s === 'true' || s === 'false' || s === 'undefined') return null;
  return s;
}

function buildGraphData(watchlist, companyById) {
  const nodeIds = new Set();
  const edges = [];
  const companyTypeById = {};

  (watchlist || []).forEach((item) => {
    const startCompanyId = normalizeCompanyId(item.companyId ?? item.company_id);
    if (!startCompanyId) return;
    const info = item.userCompanyInfo;
    const rawType = (info?.companyType ?? info?.company_type ?? '').toString().trim().toLowerCase();
    companyTypeById[startCompanyId] = rawType || 'unassigned';
    nodeIds.add(startCompanyId);

    (item.userCompanyInfo?.routes ?? []).forEach((route) => {
      const endCompanyId =
        normalizeCompanyId(route.endPointCompanyId ?? route.end_point_company_id) ?? startCompanyId;
      nodeIds.add(endCompanyId);
      if (!companyTypeById[endCompanyId] && endCompanyId !== startCompanyId) {
        const endItem = (watchlist || []).find((i) => (i.companyId ?? i.company_id) === endCompanyId);
        const endInfo = endItem?.userCompanyInfo;
        const endRaw = (endInfo?.companyType ?? endInfo?.company_type ?? '').toString().trim().toLowerCase();
        companyTypeById[endCompanyId] = endRaw || 'unassigned';
      }
      edges.push({
        from: startCompanyId,
        to: endCompanyId,
        routeName: route.routeName ?? route.route_name,
        routeId: route.routeId ?? route.route_id,
      });
    });
  });

  const nodes = Array.from(nodeIds).map((id) => ({
    id,
    ticker: companyById[id]?.ticker ?? companyById[id]?.id ?? id,
    name: companyById[id]?.name ?? id,
    columnId: getColumnForType(companyTypeById[id]),
  }));

  return { nodes, edges };
}

/** Half-side of the square node (axis-aligned); full side = BOX_HALF * 2 */
const BOX_HALF = 16;
const COL_W = 100;
const COL_GAP = 12;
const ROW_GAP = 10;
const HEADER_H = 32;
const PAD = 16;
const NUM_COLUMNS = COLUMNS.length;

/** Distance from square center to edge along direction (ux, uy) */
function squareEdgeDist(ux, uy) {
  const m = Math.max(Math.abs(ux), Math.abs(uy)) || 1;
  return BOX_HALF / m;
}

export default function CompanyConnectionsGraph({ watchlist = [], companies = [] }) {
  const navigate = useNavigate();
  const { t } = useLocale();

  const companyById = useMemo(() => {
    const m = {};
    (companies || []).forEach((c) => {
      if (c.id) m[c.id] = c;
      if (c.ticker) m[c.ticker] = c;
    });
    return m;
  }, [companies]);

  const { nodes, edges } = useMemo(() => buildGraphData(watchlist, companyById), [watchlist, companyById]);

  /** All 5 Kanban columns, each with its nodes (may be empty) */
  const graphColumns = useMemo(
    () => COLUMNS.map((col) => ({ ...col, nodes: nodes.filter((n) => n.columnId === col.id) })),
    [nodes]
  );

  const nodePositionMap = useMemo(() => {
    const m = {};
    graphColumns.forEach((col, colIndex) => {
      const centerX = PAD + COL_W / 2 + colIndex * (COL_W + COL_GAP);
      col.nodes.forEach((node, rowIndex) => {
        const y = HEADER_H + PAD + BOX_HALF + rowIndex * (BOX_HALF * 2 + ROW_GAP);
        m[node.id] = { ...node, x: centerX, y };
      });
    });
    return m;
  }, [graphColumns]);

  const hasConnections = edges.length > 0;
  const cardStyle = useMemo(
    () => ({
      backgroundColor: colors.background.card ?? colors.background.secondary,
      borderColor: colors.border.light,
      borderRadius: spacing.radius?.lg ?? 12,
      boxShadow: spacing.shadow?.lg ?? '0 6px 18px rgba(32,41,50,0.04)',
    }),
    []
  );
  const titleStyle = typography.textStyles?.label || typography.textStyles?.cardTitle || {};
  const bodyStyle = typography.textStyles?.caption || typography.textStyles?.bodySmall || {};
  const columnLabelStyle = typography.textStyles?.caption || { fontSize: '0.6875rem', fontWeight: 500 };
  const nodeLabelFontSize = '0.625rem'; // 10px – fits small node box; from design system scale

  const svgW = NUM_COLUMNS * COL_W + (NUM_COLUMNS - 1) * COL_GAP + 2 * PAD;
  const svgH = 340;

  return (
    <Card style={cardStyle} className="overflow-hidden w-full">
      <style>{`
        @keyframes bubble-float {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-2px); opacity: 0.92; }
        }
        @keyframes flow-dash {
          to { stroke-dashoffset: 0; }
        }
        .company-graph-bubble {
          animation: bubble-float 4s ease-in-out infinite;
        }
        .company-graph-bubble:nth-child(odd) { animation-delay: 0.2s; }
        .company-graph-bubble:nth-child(3n) { animation-delay: 0.4s; }
        .company-graph-flow-path {
          stroke-dasharray: 8 6;
          animation: flow-dash 0.6s ease-out forwards;
        }
      `}</style>
      <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: colors.border.light }}>
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: colors.primary[100], color: colors.primary[600] }}
        >
          <Network className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold" style={{ color: colors.text.primary, ...titleStyle }}>
            {t('companyConnectionsTitle')}
          </h3>
          <p className="text-xs" style={{ color: colors.text.tertiary, ...bodyStyle }}>
            {t('companyConnectionsDescription')}
          </p>
        </div>
      </div>

      <div className="p-4">
        {!hasConnections && nodes.length === 0 ? (
          <div
            className="rounded-xl border border-dashed flex flex-col items-center justify-center py-12 px-4"
            style={{
              borderColor: colors.border.light,
              backgroundColor: colors.background.secondary ?? colors.gray[50],
              minHeight: 320,
            }}
          >
            <Building2 className="h-12 w-12 mb-3" style={{ color: colors.gray[400] }} />
            <p className="text-sm text-center" style={{ color: colors.text.secondary }}>
              {t('companyConnectionsEmpty')}
            </p>
          </div>
        ) : !hasConnections ? (
          <div
            className="rounded-xl border border-dashed flex flex-col items-center justify-center py-12 px-4"
            style={{
              borderColor: colors.border.light,
              backgroundColor: colors.background.secondary ?? colors.gray[50],
              minHeight: 320,
            }}
          >
            <Network className="h-12 w-12 mb-3" style={{ color: colors.gray[400] }} />
            <p className="text-sm text-center" style={{ color: colors.text.secondary }}>
              {t('companyConnectionsNoRoutes')}
            </p>
          </div>
        ) : (
          <div
            className="w-full rounded-xl overflow-hidden overflow-x-auto"
            style={{ backgroundColor: colors.gray[50], minHeight: svgH }}
          >
            <svg
              viewBox={`0 0 ${svgW} ${svgH}`}
              className="w-full"
              style={{ minHeight: svgH, display: 'block' }}
              preserveAspectRatio="xMidYMid meet"
              aria-label={t('companyConnectionsTitle')}
            >
              {/* Curved edges (flow) */}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                  fill={colors.primary[400]}
                >
                  <polygon points="0 0, 10 3.5, 0 7" />
                </marker>
                <marker
                  id="arrowhead-dim"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                  fill={colors.gray[400]}
                >
                  <polygon points="0 0, 10 3.5, 0 7" />
                </marker>
              </defs>
              <g>
                {edges.map((e, idx) => {
                  const fromNode = nodePositionMap[e.from];
                  const toNode = nodePositionMap[e.to];
                  if (!fromNode || !toNode) return null;
                  const isSelf = e.from === e.to;
                  let pathD;
                  if (isSelf) {
                    pathD = `M ${fromNode.x} ${fromNode.y - BOX_HALF} A 18 18 0 1 1 ${fromNode.x + 0.01} ${fromNode.y - BOX_HALF}`;
                  } else {
                    const dx = toNode.x - fromNode.x;
                    const dy = toNode.y - fromNode.y;
                    const dist = Math.hypot(dx, dy) || 1;
                    const ux = dx / dist;
                    const uy = dy / dist;
                    const fromTrim = squareEdgeDist(ux, uy);
                    const toTrim = squareEdgeDist(-ux, -uy);
                    const startX = fromNode.x + ux * fromTrim;
                    const startY = fromNode.y + uy * fromTrim;
                    const endX = toNode.x - ux * toTrim;
                    const endY = toNode.y - uy * toTrim;
                    const midX = (startX + endX) / 2;
                    const midY = (startY + endY) / 2;
                    const ctrlOffset = Math.min(60, Math.abs(endX - startX) * 0.4);
                    const ctrlX = midX + (startY < endY ? ctrlOffset : -ctrlOffset);
                    const ctrlY = midY - (startX < endX ? ctrlOffset : -ctrlOffset);
                    pathD = `M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`;
                  }
                  return (
                    <path
                      key={`edge-${idx}-${e.routeId || idx}`}
                      d={pathD}
                      fill="none"
                      stroke={isSelf ? colors.gray[400] : colors.primary[300]}
                      strokeWidth={2}
                      strokeDasharray={isSelf ? '4 3' : '0'}
                      opacity={0.85}
                      markerEnd={isSelf ? 'none' : `url(#arrowhead)`}
                      className="company-graph-flow-path"
                      style={{ strokeDashoffset: isSelf ? 0 : 24 }}
                    />
                  );
                })}
              </g>
              {/* Column headers – all 5 Kanban columns */}
              {graphColumns.map((col, colIndex) => {
                const centerX = PAD + COL_W / 2 + colIndex * (COL_W + COL_GAP);
                const label = t(col.labelKey);
                const shortLabel = label.length > 12 ? label.slice(0, 10) + '…' : label;
                return (
                  <g key={col.id}>
                    <rect
                      x={centerX - COL_W / 2 + 2}
                      y={6}
                      width={COL_W - 4}
                      height={HEADER_H - 10}
                      rx={6}
                      fill={col.color}
                      fillOpacity={0.12}
                      stroke={col.color}
                      strokeOpacity={0.35}
                      strokeWidth={1}
                    />
                    <text
                      x={centerX}
                      y={HEADER_H / 2 + 1}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fill: colors.text.secondary,
                        fontSize: columnLabelStyle.fontSize ?? '0.6875rem',
                        fontWeight: columnLabelStyle.fontWeight ?? 500,
                      }}
                    >
                      {shortLabel}
                    </text>
                  </g>
                );
              })}
              {/* Square nodes (ticker) */}
              <g>
                {Object.values(nodePositionMap).map((node, idx) => {
                  const col = COLUMNS.find((c) => c.id === node.columnId);
                  const colColor = col?.color ?? colors.gray[500];
                  const routeTicker = String(node.ticker ?? node.id ?? '').trim();
                  const isValidTicker =
                    routeTicker &&
                    routeTicker !== 'true' &&
                    routeTicker !== 'false' &&
                    routeTicker !== 'undefined';
                  const label = routeTicker.length > 5 ? `${routeTicker.slice(0, 4)}…` : routeTicker;
                  const goToCompany = () => {
                    if (!isValidTicker) return;
                    navigate(`/companies/${encodeURIComponent(routeTicker)}`);
                  };
                  return (
                    <g
                      key={node.id}
                      className="company-graph-bubble"
                      style={{ animationDelay: `${idx * 0.1}s`, cursor: isValidTicker ? 'pointer' : 'default' }}
                      onClick={goToCompany}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          goToCompany();
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={t('companyConnectionsGoToCompany', { name: node.name })}
                    >
                      <rect
                        x={node.x - BOX_HALF}
                        y={node.y - BOX_HALF}
                        width={BOX_HALF * 2}
                        height={BOX_HALF * 2}
                        rx={4}
                        fill={colors.background.card ?? '#fff'}
                        stroke={colColor}
                        strokeWidth={2}
                        className="transition-all duration-300 hover:opacity-95"
                        style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.08))' }}
                      />
                      <text
                        x={node.x}
                        y={node.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="pointer-events-none select-none"
                        style={{
                          fill: colors.text.primary,
                          fontSize: nodeLabelFontSize,
                          fontWeight: typography.fontWeight?.medium ?? 500,
                        }}
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
            <div
              className="px-3 py-2 border-t flex items-center justify-between flex-wrap gap-2"
              style={{ borderColor: colors.border.light, backgroundColor: colors.background.card }}
            >
              <span className="text-xs" style={{ color: colors.text.tertiary }}>
                {t('companyConnectionsLegend', { count: nodes.length, routes: edges.length })}
              </span>
              <span className="text-xs" style={{ color: colors.text.tertiary }}>
                {t('companyConnectionsClickHint')}
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
